/**
 * PlotexCameraModeSwitcher — toggle between OrbitControls and FPS walk mode.
 *
 * Arch-viz scenes benefit from two complementary navigation models: orbit for
 * surveying the building from the outside, and first-person walk for stepping
 * through interiors at eye-level. This widget owns BOTH control instances and
 * flips `enabled` + pointer-lock state whenever the user toggles the toolbar
 * button (or presses Escape to break out of lock).
 *
 * Why a single class owns both controls: pointer-lock entry MUST happen inside
 * a user-gesture handler (the button click), and the two controls need to know
 * about each other so only one is ever authoritative for input. Threading that
 * through `createPlotexEngine` as two separate pieces got untidy fast.
 *
 * Why we listen to the native `pointerlockchange` event too: the browser may
 * strip the lock without going through our button (Esc key, tab switch, error
 * recovery). If we relied solely on our own state we'd end up with
 * `PointerLockControls.unlock()` called but `OrbitControls.enabled=false` — a
 * dead-input state where the user couldn't navigate at all.
 */

import type { PerspectiveCamera } from 'three';
import { Vector3 } from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';

export type PlotexCameraMode = 'orbit' | 'walk';

export interface PlotexCameraModeSwitcherOptions {
  camera: PerspectiveCamera;
  controls: OrbitControls;
  /**
   * Element used by PointerLockControls for pointer-lock acquisition. Should
   * be the renderer's canvas — locking on `document.body` works but captures
   * the cursor over DOM chrome (toolbar buttons) too, which feels wrong.
   */
  canvas: HTMLCanvasElement;
  /** Walk-mode base speed in meters/second. Default: 5 (typical walking pace). */
  speed?: number;
  /**
   * Sprint speed while Shift is held, in meters/second. Default: 12 (brisk run).
   * Chosen to be roughly 2.4× base so the feel maps to a platformer's
   * walk/sprint ratio rather than feeling like teleport.
   */
  sprintSpeed?: number;
}

export class PlotexCameraModeSwitcher {
  private camera: PerspectiveCamera;
  private orbit: OrbitControls;
  private pointerLock: PointerLockControls;
  private canvas: HTMLCanvasElement;

  private mode: PlotexCameraMode = 'orbit';
  private speed: number;
  private sprintSpeed: number;

  // Input state. Each key maps to a boolean that pressure-tracks its physical
  // key state (down=true until up). We intentionally don't clear keys on mode
  // switch — if the user was holding W when they toggled back to orbit, they
  // probably don't want to "carry" that forward motion back into walk mode
  // when they switch again, so the keyup will handle it naturally.
  private keyForward = false;
  private keyBackward = false;
  private keyLeft = false;
  private keyRight = false;
  // Q/E are optional in the spec — shipping them because arch-viz users
  // routinely want to pop up a level to see mezzanines/balconies without
  // fiddling with mouselook-induced head tilt.
  private keyUp = false;
  private keyDown = false;
  private sprint = false;

  // Scratch vectors for the per-frame movement integration. Allocated once
  // in the constructor to keep update() allocation-free at 60 Hz.
  private forwardDir = new Vector3();
  private rightDir = new Vector3();
  private worldUp = new Vector3(0, 1, 0);
  private moveDelta = new Vector3();

  private button: HTMLButtonElement;

  // Bound handler refs so removeEventListener actually unbinds them. Arrow
  // functions assigned at construction time keep a stable identity across
  // the object's lifetime.
  private onKeyDownBound: (e: KeyboardEvent) => void;
  private onKeyUpBound: (e: KeyboardEvent) => void;
  private onLockBound: () => void;
  private onUnlockBound: () => void;

  constructor(opts: PlotexCameraModeSwitcherOptions) {
    this.camera = opts.camera;
    this.orbit = opts.controls;
    this.canvas = opts.canvas;
    this.speed = opts.speed ?? 5;
    this.sprintSpeed = opts.sprintSpeed ?? 12;

    // PointerLockControls attaches mousemove listeners to document when
    // locked, and fires lock/unlock events we hook below. Passing the
    // canvas as the domElement scopes the click-to-lock gesture properly.
    this.pointerLock = new PointerLockControls(this.camera, this.canvas);

    this.onKeyDownBound = (e) => this._onKeyDown(e);
    this.onKeyUpBound = (e) => this._onKeyUp(e);
    this.onLockBound = () => this._onLock();
    this.onUnlockBound = () => this._onUnlock();

    // PointerLockControls fires lock/unlock on its own internal dispatcher
    // AND the document itself fires `pointerlockchange`. We listen to the
    // control's events because they're already normalized across browsers —
    // the control's constructor wires the raw DOM event for us.
    this.pointerLock.addEventListener('lock', this.onLockBound);
    this.pointerLock.addEventListener('unlock', this.onUnlockBound);

    this.button = this._buildToolbarButton();
    document.body.appendChild(this.button);
  }

  // -- Public API -----------------------------------------------------------

  /**
   * Switch mode explicitly. Safe to call from any context; walk-mode entry
   * will only succeed if triggered by a user gesture (button click, key
   * press) because `pointerLock.lock()` gets rejected by the browser
   * otherwise. Programmatic `setMode('walk')` from a timer will be ignored
   * by the browser with a console warning — that's on the caller, not us.
   */
  setMode(mode: PlotexCameraMode): void {
    if (mode === this.mode) return;

    if (mode === 'walk') {
      // Hand over authority to PointerLockControls. We disable OrbitControls
      // BEFORE requesting the lock so the two don't briefly race for mouse
      // input in the same frame.
      this.orbit.enabled = false;
      this._attachKeyboardListeners();
      // lock() must run inside a user-gesture callback; the button click
      // qualifies, as does any keydown. If the browser rejects the request,
      // the `unlock` event will never fire and we'll fall back to orbit
      // via onUnlock() — but we preemptively set mode so the button reads
      // the intended state.
      this.mode = 'walk';
      this._setButtonActive(true);
      this.pointerLock.lock();
    } else {
      // Orbit mode — release pointer lock and re-enable orbit input.
      // unlock() is safe to call even if not locked; the internals guard
      // against that case.
      this._detachKeyboardListeners();
      this._clearKeys();
      if (this.pointerLock.isLocked) this.pointerLock.unlock();
      this.orbit.enabled = true;
      this.mode = 'orbit';
      this._setButtonActive(false);
    }
  }

  getMode(): PlotexCameraMode {
    return this.mode;
  }

  /**
   * Per-frame movement integration. Call this from the engine's animate
   * loop BEFORE render(). `dt` is the frame delta in seconds — computing it
   * outside the switcher lets the engine share a single `performance.now()`
   * sample across its handful of time-sensitive subsystems.
   *
   * The walk-mode motion uses the CAMERA's forward + right, not the XZ
   * projection of pointer-lock's moveForward helper, so looking up and
   * pressing W actually flies you up. For arch-viz this is the useful
   * behavior (think drone / free-look rather than human-anatomy walk).
   * The spec calls this out: "movement direction is driven by the camera's
   * forward + right vectors (so you move in the direction you're looking)".
   */
  update(dt: number): void {
    if (this.mode !== 'walk') return;
    // No-op when the lock is pending / revoked. Moving the camera while
    // unlocked would feel like ghost input to the user.
    if (!this.pointerLock.isLocked) return;
    if (dt <= 0) return;
    // Clamp dt to avoid a massive jump after a tab-switch / debug pause.
    // 100 ms = 0.5 s of movement at sprint speed, which is enough to feel
    // responsive without teleporting the camera across the building.
    const safeDt = Math.min(dt, 0.1);

    const currentSpeed = this.sprint ? this.sprintSpeed : this.speed;

    // Derive movement basis from the camera's current orientation. We call
    // getWorldDirection into a scratch vec to avoid allocating a fresh
    // Vector3 per frame — critical at 60 Hz across long sessions.
    this.camera.getWorldDirection(this.forwardDir);
    // Right = forward × worldUp (not camera.up, which may have tilted via
    // OrbitControls dragging). Keeps strafing horizontal even if the
    // camera is looking steeply up or down.
    this.rightDir.crossVectors(this.forwardDir, this.worldUp).normalize();

    this.moveDelta.set(0, 0, 0);
    if (this.keyForward) this.moveDelta.add(this.forwardDir);
    if (this.keyBackward) this.moveDelta.sub(this.forwardDir);
    if (this.keyRight) this.moveDelta.add(this.rightDir);
    if (this.keyLeft) this.moveDelta.sub(this.rightDir);
    if (this.keyUp) this.moveDelta.y += 1;
    if (this.keyDown) this.moveDelta.y -= 1;

    // If no keys are down skip the work. moveDelta.lengthSq() is slightly
    // cheaper than isZero() for this common case.
    if (this.moveDelta.lengthSq() === 0) return;

    // Normalize so diagonal movement (W+D) isn't faster than axis-aligned
    // movement. Then scale by speed * dt for framerate-independent motion.
    this.moveDelta.normalize().multiplyScalar(currentSpeed * safeDt);
    this.camera.position.add(this.moveDelta);
  }

  dispose(): void {
    // Tear down in reverse-construction order: listeners first (so no
    // stray events fire during DOM cleanup), then the DOM button, then
    // the pointer-lock control itself.
    this._detachKeyboardListeners();
    this.pointerLock.removeEventListener('lock', this.onLockBound);
    this.pointerLock.removeEventListener('unlock', this.onUnlockBound);
    // Make sure we leave the orbit controls in a usable state — disposing
    // while in walk mode would otherwise leak the disabled flag.
    if (this.mode === 'walk') {
      if (this.pointerLock.isLocked) this.pointerLock.unlock();
      this.orbit.enabled = true;
    }
    this.pointerLock.dispose();
    this.button.remove();
  }

  // -- Internal -------------------------------------------------------------

  private _attachKeyboardListeners(): void {
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('keyup', this.onKeyUpBound);
  }

  private _detachKeyboardListeners(): void {
    window.removeEventListener('keydown', this.onKeyDownBound);
    window.removeEventListener('keyup', this.onKeyUpBound);
  }

  /**
   * Drop all key state. Called on mode exit so a held key doesn't "stick"
   * when the user toggles back into walk mode — the keyup for it would
   * have fired in orbit mode where we weren't listening.
   */
  private _clearKeys(): void {
    this.keyForward = false;
    this.keyBackward = false;
    this.keyLeft = false;
    this.keyRight = false;
    this.keyUp = false;
    this.keyDown = false;
    this.sprint = false;
  }

  private _onKeyDown(e: KeyboardEvent): void {
    // Use e.code (physical key) rather than e.key (logical key) so AZERTY
    // users and other layouts still get WASD on their W/A/S/D positions.
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.keyForward = true;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.keyBackward = true;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.keyLeft = true;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.keyRight = true;
        break;
      case 'KeyQ':
        this.keyUp = true;
        break;
      case 'KeyE':
        this.keyDown = true;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.sprint = true;
        break;
      default:
        return;
    }
  }

  private _onKeyUp(e: KeyboardEvent): void {
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        this.keyForward = false;
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.keyBackward = false;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.keyLeft = false;
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.keyRight = false;
        break;
      case 'KeyQ':
        this.keyUp = false;
        break;
      case 'KeyE':
        this.keyDown = false;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.sprint = false;
        break;
      default:
        return;
    }
  }

  /**
   * Fires when PointerLockControls successfully acquires the lock. Our
   * own setMode('walk') already set the internal state; this is a no-op
   * confirmation path. We DO refresh the button just in case the lock
   * was triggered externally (rare — only if some host code calls
   * `pointerLock.lock()` directly bypassing us).
   */
  private _onLock(): void {
    this.mode = 'walk';
    this.orbit.enabled = false;
    this._attachKeyboardListeners();
    this._setButtonActive(true);
  }

  /**
   * Fires when the browser releases the lock (Esc, tab switch, window
   * blur, or our own unlock() call). We unconditionally return to orbit
   * so the user is never stranded without working input.
   */
  private _onUnlock(): void {
    this._detachKeyboardListeners();
    this._clearKeys();
    this.orbit.enabled = true;
    this.mode = 'orbit';
    this._setButtonActive(false);
  }

  // -- Toolbar button -------------------------------------------------------

  private _buildToolbarButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = 'Walk';
    btn.setAttribute('aria-pressed', 'false');
    btn.title = 'Walk mode (WASD + mouse look; Shift to sprint; Esc to exit)';
    Object.assign(btn.style, {
      position: 'fixed',
      // Stacked above the measure button (at bottom:80px). 16 px button +
      // 16 px gap between buttons gives a clean toolbar strip; 144 =
      // 80 (measure) + 40 (height) + 24 (gap). Matches the spec hint.
      bottom: '144px',
      right: '16px',
      zIndex: '9999',
      width: '40px',
      height: '40px',
      padding: '0',
      borderRadius: '8px',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      background: 'rgba(0, 0, 0, 0.55)',
      color: '#ccc',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '10px',
      fontWeight: '500',
      letterSpacing: '0.2px',
      transition: 'background 0.15s ease, color 0.15s ease',
      userSelect: 'none',
    });
    btn.addEventListener('click', () => {
      // Click toggles the destination mode. We flip based on current state,
      // not the button's text, because the `pointerlockchange` handler may
      // have desynced the text from the real mode transiently.
      this.setMode(this.mode === 'walk' ? 'orbit' : 'walk');
    });
    btn.addEventListener('mouseenter', () => {
      if (this.mode !== 'walk') btn.style.background = 'rgba(255, 255, 255, 0.15)';
    });
    btn.addEventListener('mouseleave', () => {
      if (this.mode !== 'walk') btn.style.background = 'rgba(0, 0, 0, 0.55)';
    });
    return btn;
  }

  private _setButtonActive(active: boolean): void {
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (active) {
      // Active state highlights blue AND flips the label to the exit
      // action, so the button reads as "what happens if I click now" —
      // mirrors the pattern the measure-tool button establishes.
      this.button.style.background = '#3b82f6';
      this.button.style.color = '#fff';
      this.button.textContent = 'Orbit';
    } else {
      this.button.style.background = 'rgba(0, 0, 0, 0.55)';
      this.button.style.color = '#ccc';
      this.button.textContent = 'Walk';
    }
  }
}
